/** A right-hand side panel: the primitive behind every "open this beside the list" interaction.
 *
 * It is a modal dialog: a scrim, `role="dialog"` with `aria-modal`, a name from its title, focus
 * moved in on open and trapped while open, Escape and the scrim and the close button all close it,
 * and focus goes back to **whatever opened it** (captured at open, not a remembered selector - the
 * lesson `ReceiptDrawer` records). It slides in with `motion` and under reduced motion simply
 * appears. It is portalled to `document.body` so the shell's `overflow: hidden` cannot clip it.
 *
 * `ReceiptDrawer` is a different, non-modal region that sits beside the screen and is left as it is;
 * this is for panels that take the screen's focus (a row's detail, a filter set, a form).
 */

import { X } from "@phosphor-icons/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useId, useRef } from "react";
import type { ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";

import styles from "./Drawer.module.css";

/** `--dur-3` and `--dur-2`, in seconds. */
export const DRAWER_IN_S = 0.24;
export const DRAWER_OUT_S = 0.14;

export type DrawerWidth = "sm" | "md" | "lg";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (node) => !node.closest("[inert], [hidden]") && node.getAttribute("aria-hidden") !== "true",
  );
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** The dialog's name. Required: a dialog with no name is announced as nothing. */
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Pinned under the body: the primary action. */
  footer?: ReactNode;
  width?: DrawerWidth;
  /** Focus this on open instead of the panel itself. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Return focus here instead of to whatever was focused at open. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  testId?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = "md",
  initialFocusRef,
  returnFocusRef,
  testId = "drawer",
}: DrawerProps) {
  const reduce = useReducedMotion();
  const titleId = useId();
  const descId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // One effect, so the order is ours: focus moves in and the listeners go on at open; at close the
  // listeners come off *before* focus goes back (a still-attached focusin guard would pull it back).
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (initialFocusRef?.current ?? panel.current)?.focus();
    const explicit = returnFocusRef?.current;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel.current) return;
      const nodes = focusables(panel.current);
      if (nodes.length === 0) {
        event.preventDefault();
        panel.current.focus();
        return;
      }
      const first = nodes[0] as HTMLElement;
      const last = nodes[nodes.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onFocusIn = (event: FocusEvent) => {
      if (!panel.current || !(event.target instanceof Node) || panel.current.contains(event.target)) return;
      // A modal opened over this one (the lease-correction dialog is portalled to the body) owns
      // focus while it is open and holds it with a guard of its own. Pulling focus out of it from
      // here makes the two guards answer each other without end: Chromium suppresses the re-entrant
      // focus event, WebKit and Firefox overflow the stack and the field never receives its input.
      if (event.target instanceof Element && event.target.closest('[aria-modal="true"]')) return;
      panel.current.focus();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocusIn);
      const back = explicit ?? opener;
      if (back?.isConnected && back !== document.body) back.focus();
    };
  }, [open, initialFocusRef, returnFocusRef]);

  // The motion tokens' numbers (`--dur-3` 240ms in, `--dur-2` 140ms out, `--ease-out`, `--ease-exit` in
  // tokens.css): the library takes numbers, not custom properties, so they are repeated here and
  // `Drawer.test.tsx` holds them to the stylesheet. Reduced motion is no motion.
  const slide = reduce
    ? { duration: 0 }
    : ({ duration: DRAWER_IN_S, ease: [0.16, 1, 0.3, 1] } as const);
  const slideOut = reduce
    ? { duration: 0 }
    : ({ duration: DRAWER_OUT_S, ease: [0.2, 0, 1, 0.9] } as const);
  const fade = reduce ? { duration: 0 } : ({ duration: DRAWER_OUT_S } as const);

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className={styles.root} key="drawer" data-testid={`${testId}-root`}>
          <motion.div
            role="presentation"
            className={styles.scrim}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fade}
          />
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
            tabIndex={-1}
            className={`${styles.panel} ${styles[width]}`}
            data-testid={testId}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%", transition: slideOut }}
            transition={slide}
          >
            <header className={styles.head}>
              <div className={styles.heading}>
                <h2 id={titleId} className={styles.title}>
                  {title}
                </h2>
                {description ? (
                  <p id={descId} className={styles.description}>
                    {description}
                  </p>
                ) : null}
              </div>
              <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
                <X size={18} aria-hidden="true" />
              </button>
            </header>
            <div className={styles.body}>{children}</div>
            {footer ? <footer className={styles.footer}>{footer}</footer> : null}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
